<template>
	<v-card>
		<v-card-title>{{ form.id ? '编辑样本' : '添加样本' }}</v-card-title>
		<v-card-text>
			<v-form ref="sampleForm">
				<v-select
					v-model="form.cupping_id"
					:items="cuppings"
					item-title="label"
					item-value="id"
					label="所属杯测"
					density="compact"
					hide-details="auto"
					class="mb-3"
					:error-messages="errors?.cupping_id"
				></v-select>

				<div class="d-flex align-center flex-wrap ga-2 mb-1">
					<bean-batch-selector bean-type="roasted" @selected="onBeanBatchSelected">
						选择熟豆批次
					</bean-batch-selector>
					<span v-if="selectedBean" class="text-body-2 font-weight-medium">{{ selectedBean.name }}</span>
					<span v-if="selectedBeanBatch" class="text-body-2 text-medium-emphasis">
						{{ selectedBeanBatch.date }} {{ selectedBeanBatch.type }}
					</span>
				</div>
				<v-alert v-if="errors?.bean_id" type="error" density="compact" class="mb-2">{{ errors?.bean_id }}</v-alert>
				<v-alert v-if="errors?.scores" type="error" density="compact" class="mb-2">{{ errors?.scores }}</v-alert>
				<v-alert v-if="errors?.beanbatch_id" type="error" density="compact" class="mb-2">
					beanbatch_id: {{ errors?.beanbatch_id }}
				</v-alert>

				<v-row dense>
					<v-col cols="6">
						<v-text-field
							v-model.number="form.beanqty"
							label="数量"
							type="number"
							density="compact"
							hide-details="auto"
							:error-messages="errors?.beanqty"
						>
							<template #append-inner>g</template>
						</v-text-field>
					</v-col>
					<v-col cols="6">
						<v-select
							v-model="form.method"
							:items="refs['CuppingMethod']"
							item-title="title"
							item-value="value"
							label="杯测方法"
							density="compact"
							hide-details="auto"
							:error-messages="errors?.method"
						></v-select>
					</v-col>
					<v-col cols="12" sm="7">
						<v-combobox
							v-model="form.grinder_name"
							v-model:search="grinderSearch"
							:items="grinderNames"
							label="磨豆机"
							hint="输入可筛选已有磨豆机，也可直接输入新名称"
							persistent-hint
							clearable
							no-filter
							density="compact"
							hide-details="auto"
							:loading="grinderLoading"
							:error-messages="errors?.grinder_name"
							@update:search="onGrinderSearch"
						></v-combobox>
					</v-col>
					<v-col cols="12" sm="5">
						<v-text-field
							v-model="form.grindsize"
							label="研磨刻度"
							density="compact"
							hide-details="auto"
							:error-messages="errors?.grindsize"
						></v-text-field>
					</v-col>
					<v-col cols="4">
						<v-text-field
							v-model.number="form.watertemp"
							label="水温"
							type="number"
							density="compact"
							hide-details="auto"
							:error-messages="errors?.watertemp"
						>
							<template #append-inner>°C</template>
						</v-text-field>
					</v-col>
					<v-col cols="4">
						<v-text-field
							v-model.number="form.watertds"
							label="TDS水质"
							type="number"
							density="compact"
							hide-details="auto"
							:error-messages="errors?.watertds"
						></v-text-field>
					</v-col>
					<v-col cols="4">
						<v-text-field
							v-model="form.extractionyield"
							label="萃取率"
							density="compact"
							hide-details="auto"
							:error-messages="errors?.extractionyield"
						></v-text-field>
					</v-col>
					<v-col cols="12">
						<v-textarea
							v-model="form.memo"
							label="备注"
							density="compact"
							rows="2"
							auto-grow
							hide-details="auto"
							:error-messages="errors?.memo"
						></v-textarea>
					</v-col>
				</v-row>
			</v-form>
		</v-card-text>
		<v-card-actions>
			<slot name="actions" :object="form"></slot>
		</v-card-actions>
	</v-card>
</template>

<script>
import BeanBatchSelector from '../bean/BatchSelector.vue'
import BeanSelector from '../bean/Selector.vue'
import RoastSelector from '../roast/Selector.vue'
import { clone } from 'lodash-es'

export default {
	name: 'CuppingSampleForm',
	components: { BeanSelector, RoastSelector, BeanBatchSelector },
	props: {
		value: Object,
		errors: {
			type: Object,
			default: () => ({})
		}
	},
	data() {
		return {
			form: {},
			refs: {},
			selectedBean: null,
			selectedBeanBatch: null,
			cuppings: [],
			grinderNames: [],
			grinderSearch: '',
			grinderLoading: false,
			grinderSearchTimer: null
		}
	},
	beforeUnmount() {
		if (this.grinderSearchTimer) clearTimeout(this.grinderSearchTimer)
	},
	mounted() {
		this.form = clone(this.value || {})
		if (this.form.beanbatch) {
			this.selectedBean = this.form.bean
			this.selectedBeanBatch = this.form.beanbatch
		}
		this.$api.common.meta('cupping', 'CuppingMethod', (res) => {
			this.refs = res
		})
		if (!this.form.method) {
			this.form.method = 'standard'
		}
		this.loadGrinderNames()
		this.loadCuppings()
	},
	watch: {
		value: {
			handler(newVal) {
				this.form = clone(newVal || {})
				if (this.form.beanbatch) {
					this.selectedBean = this.form.bean
					this.selectedBeanBatch = this.form.beanbatch
				}
			}
		}
	},
	methods: {
		cuppingOption(item) {
			return {
				...item,
				label: item.location ? `${item.location}（#${item.id}）` : `杯测 #${item.id}`
			}
		},
		loadCuppings() {
			this.$api.cupping.search({ pageno: 1, pagesize: 200 }, (res) => {
				const list = (res.objects || []).map((item) => this.cuppingOption(item))
				const id = Number(this.form?.cupping_id || 0)
				if (!id || list.some((item) => item.id === id)) {
					this.cuppings = list
					return
				}
				const embedded = this.form?.cupping
				if (embedded?.id === id) {
					list.unshift(this.cuppingOption(embedded))
					this.cuppings = list
					return
				}
				this.$api.cupping.getById(id, (item) => {
					if (item?.id) list.unshift(this.cuppingOption(item))
					this.cuppings = list
				})
			})
		},
		loadGrinderNames(name = '') {
			this.grinderLoading = true
			this.$api.metadata.search(
				{ group: 'grinder', status: 'valid', name: name || undefined, pagesize: 200 },
				(res) => {
					this.grinderLoading = false
					this.grinderNames = (res.objects || []).map((g) => g.name).filter(Boolean)
				},
				() => {
					this.grinderLoading = false
				}
			)
		},
		onGrinderSearch(q) {
			// combobox 仅回车/点选才提交 model，输入先同步到 grinder_name，保证提交时后端能收到并沉淀
			if (typeof q === 'string') this.form.grinder_name = q
			if (this.grinderSearchTimer) clearTimeout(this.grinderSearchTimer)
			this.grinderSearchTimer = setTimeout(() => {
				this.loadGrinderNames((q || '').trim())
			}, 300)
		},
		onBeanBatchSelected(ids, os) {
			if (ids && ids.length > 0) {
				this.selectedBeanBatch = os[0]
				this.selectedBean = this.selectedBeanBatch.bean
				this.form.beanbatch_id = ids[0]
				this.form.beanbatch = os[0]
				this.form.bean_id = this.form.beanbatch.bean_id
				this.form.bean = this.form.beanbatch.bean
			} else {
				this.selectedBeanBatch = null
				this.selectedBean = null
			}
		}
	}
}
</script>
